'use client';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import Image from 'next/image';
import FilterSidebar, { FilterState } from './FilterSidebar';
import SearchHeader from './SearchHeader';
import SearchResultsGrid from './SearchResultsGrid';
import ActiveFilterChips from './ActiveFilterChips';
import CompareBar from './CompareBar';
import MapView from './MapView';
import CommuteSearchPanel from './CommuteSearchPanel';
import { SCHOOLS, estimateTravelMinutes } from '@/data/schools';
import { DUBAI_COMMUNITIES } from '@/data/schools';
import type { School } from '@/data/schools';
import { khdaBannerColor } from '@/data/schools';
import Link from 'next/link';
import { Heart, ArrowRight } from 'lucide-react';
import { getClosestCommunitiesWithPrices, formatCommuteTime } from '@/data/communityPrices';
import {
  trackSchoolSearch,
  trackAddToShortlist,
  trackRemoveFromShortlist,
} from '@/lib/analytics/ga4Events';

const STORAGE_KEY = 'schoolmapper_compare_list';
const MAX_COMPARE = 10;

const DEFAULT_FILTERS: FilterState = {
  curricula: [],
  khdaRatings: [],
  feeMin: 0,
  feeMax: 999999,
  startingFeeMin: 0,
  startingFeeMax: 999999,
  finalYearFeeMin: 0,
  finalYearFeeMax: 999999,
  yearGroups: [],
  community: '',
  busRequired: false,
  genderPolicy: [],
  schoolType: [],
  applicationStatus: [],
  senSupport: false,
  maxCommuteMinutes: 999,
  studentCountMax: 99999,
};

const KHDA_ORDER: Record<string, number> = {
  Outstanding: 5, 'Very Good': 4, Good: 3, Acceptable: 2, Weak: 1, 'Very Weak': 0,
};

function applyFilters(schools: School[], filters: FilterState, query: string, communityFromQuery: string): School[] {
  return schools.filter((s) => {
    if (query.trim() && !communityFromQuery) {
      const q = query.toLowerCase();
      if (!s.name.toLowerCase().includes(q) && !s.curriculum.toLowerCase().includes(q) && !s.community.toLowerCase().includes(q) && !s.location.toLowerCase().includes(q)) return false;
    }
    if (filters.curricula.length > 0 && !filters.curricula.includes(s.curriculum)) return false;
    if (filters.khdaRatings.length > 0 && !filters.khdaRatings.includes(s.khdaRating)) return false;
    // Annual Starting Fees filter — matches against feeRangeMin (lowest/starting fee)
    if (filters.startingFeeMin > 0 || filters.startingFeeMax < 999999) {
      if (s.feeRangeMin < filters.startingFeeMin || s.feeRangeMin > filters.startingFeeMax) return false;
    }
    // Annual Final Year Fees filter — matches against feeRangeMax (highest/final year fee)
    if (filters.finalYearFeeMin > 0 || filters.finalYearFeeMax < 999999) {
      if (s.feeRangeMax < filters.finalYearFeeMin || s.feeRangeMax > filters.finalYearFeeMax) return false;
    }
    // Use communityFromQuery (typed community) OR filters.community (sidebar/panel community)
    const activeCommunity = communityFromQuery || filters.community;
    if (filters.maxCommuteMinutes < 999 && activeCommunity) {
      const travelMins = estimateTravelMinutes(activeCommunity, s);
      if (travelMins > filters.maxCommuteMinutes) return false;
    }
    if (filters.genderPolicy.length > 0 && !filters.genderPolicy.includes(s.genderPolicy)) return false;
    if (filters.schoolType.length > 0 && !filters.schoolType.includes(s.schoolType)) return false;
    if (filters.applicationStatus.length > 0 && !filters.applicationStatus.includes(s.applicationStatus)) return false;
    if (filters.senSupport && s.senSupport && !s.senSupport.available) return false;
    if (filters.studentCountMax < 99999 && s.studentCount > filters.studentCountMax) return false;
    return true;
  });
}

// Check if any non-community filters are active
function hasActiveNonCommunityFilters(filters: FilterState): boolean {
  return (
    filters.curricula.length > 0 ||
    filters.khdaRatings.length > 0 ||
    filters.startingFeeMin > 0 ||
    filters.startingFeeMax < 999999 ||
    filters.finalYearFeeMin > 0 ||
    filters.finalYearFeeMax < 999999 ||
    filters.genderPolicy.length > 0 ||
    filters.schoolType.length > 0 ||
    filters.applicationStatus.length > 0 ||
    filters.senSupport ||
    filters.studentCountMax < 99999 ||
    filters.maxCommuteMinutes < 999
  );
}

// When a community is selected (with or without commute filter), ensure at least 30 closest schools are shown
// BUT only if no other filters are active — if other filters are active, respect them strictly
function ensureMinimumResults(schools: School[], filters: FilterState, allSchools: School[], communityFromQuery: string): School[] {
  const activeCommunity = communityFromQuery || filters.community;
  if (!activeCommunity) return schools;

  // If any non-community filters are active (curriculum, KHDA, fees, commute, etc.), respect them strictly
  // Do NOT supplement with schools that don't match those filters
  if (hasActiveNonCommunityFilters(filters)) {
    // Just sort the already-filtered schools by travel time and return them
    return [...schools].sort((a, b) => {
      const aMins = estimateTravelMinutes(activeCommunity, a);
      const bMins = estimateTravelMinutes(activeCommunity, b);
      return aMins - bMins;
    });
  }

  // No other filters active — sort all schools by travel time from the active community
  const allSorted = [...allSchools].sort((a, b) => {
    const aMins = estimateTravelMinutes(activeCommunity, a);
    const bMins = estimateTravelMinutes(activeCommunity, b);
    return aMins - bMins;
  });

  // If community typed in search bar (no commute filter), always return 30 closest
  if (communityFromQuery) {
    return allSorted.slice(0, 30);
  }

  // If community from sidebar with no commute filter, return 30 closest
  if (filters.maxCommuteMinutes >= 999) {
    return allSorted.slice(0, 30);
  }

  // If community + commute filter is active: ONLY return schools genuinely within the threshold.
  // Never supplement with schools outside the threshold — that would show schools in Deira
  // as "within 30 minutes" of Dubai Hills Estate.
  return schools;
}

function applySorting(schools: School[], sortBy: string, community: string): School[] {
  const sorted = [...schools].sort((a, b) => {
    switch (sortBy) {
      case 'rating-desc': return (KHDA_ORDER[b.khdaRating] ?? 0) - (KHDA_ORDER[a.khdaRating] ?? 0);
      case 'fee-asc': return a.feeRangeMin - b.feeRangeMin;
      case 'fee-desc': return b.feeRangeMax - a.feeRangeMax;
      case 'parent-rating': return b.rating - a.rating;
      case 'travel-asc': {
        const aMins = community ? estimateTravelMinutes(community, a) : 999;
        const bMins = community ? estimateTravelMinutes(community, b) : 999;
        return aMins - bMins;
      }
      case 'name-asc': return a.name.localeCompare(b.name);
      default: return 0;
    }
  });
  // When sorting by travel time nearest first, return the 30 closest schools
  if (sortBy === 'travel-asc' && community) {
    return sorted.slice(0, 30);
  }
  return sorted;
}

function formatFee(n: number) {
  if (n === 0) return 'N/A';
  return 'AED ' + n.toLocaleString('en-AE');
}

interface SplitMapSchoolCardProps {
  school: School;
  communityFilter: string;
  compareList: string[];
  onToggleCompare: (id: string) => void;
  onSave: (id: string) => void;
  savedList: string[];
  isSelected: boolean;
  onSelect: () => void;
}

function SplitMapSchoolCard({ school, communityFilter, compareList, onToggleCompare, onSave, savedList, isSelected, onSelect }: SplitMapSchoolCardProps) {
  const bannerColor = khdaBannerColor(school.khdaRating);
  const isComparing = compareList.includes(school.id);
  const isSaved = savedList.includes(school.id);
  const communityTravelMinsRaw = communityFilter ? estimateTravelMinutes(communityFilter, school) : null;
  const communityTravelMins = communityTravelMinsRaw !== null ? formatCommuteTime(communityTravelMinsRaw) : null;
  const closestCommunities = getClosestCommunitiesWithPrices(school.coordinates.lat, school.coordinates.lng, 2);

  return (
    <div
      onClick={onSelect}
      className={`bg-white rounded-xl border-2 cursor-pointer transition-all duration-150 p-3 ${
        isSelected ? 'shadow-md -translate-y-0.5' : 'hover:shadow-sm hover:-translate-y-0.5'
      }`}
      style={{ borderColor: isSelected ? bannerColor : '#e2e8f0' }}
    >
      <div className="flex items-start gap-2.5 mb-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
          style={{ backgroundColor: bannerColor + '18', border: `1.5px solid ${bannerColor}40` }}
        >
          <Image
            src="/assets/images/image-1786254194769.png"
            alt="School"
            width={28}
            height={28}
            className="object-contain"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm leading-tight text-[#1e2d5a] line-clamp-2">{school.name}</h4>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: bannerColor + '18', color: bannerColor }}>
              {school.khdaRating}
            </span>
            <span className="text-xs text-slate-500">{school.curriculum}</span>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onSave(school.id); }}
          className={`p-1.5 rounded-lg transition-all shrink-0 ${isSaved ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-400 hover:text-red-500'}`}
        >
          <Heart size={12} className={isSaved ? 'fill-white' : ''} />
        </button>
      </div>

      <div className="space-y-1 mb-2.5">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="#1aaa6e"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 7 12 7s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
          <span className="truncate">{school.location}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="#1aaa6e"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/></svg>
          <span className="font-semibold text-slate-700 tabular-nums">
            {school.feeRangeMin === 0 ? 'Non-fee paying' : `${formatFee(school.feeRangeMin)} – ${formatFee(school.feeRangeMax)}`}
          </span>
        </div>
        {communityFilter && communityTravelMins !== null ? (
          <div className="flex items-center gap-1.5 text-xs">
            <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="#1aaa6e"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99z"/></svg>
            <span className={`font-semibold tabular-nums ${communityTravelMins <= 15 ? 'text-emerald-600' : communityTravelMins <= 30 ? 'text-sky-600' : 'text-amber-600'}`}>
              ~{communityTravelMins} min from {communityFilter}
            </span>
          </div>
        ) : closestCommunities.length > 0 ? (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="#94a3b8"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
            <span className="text-emerald-600 font-semibold">~{formatCommuteTime(closestCommunities[0].travelMins)} min</span>
            <span>from {closestCommunities[0].name}</span>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <Link
          href={`/school-profile-comparison?school=${school.id}`}
          className="flex-1 flex items-center justify-center gap-1 py-2 text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-all active:scale-95"
          style={{ backgroundColor: bannerColor }}
        >
          View Profile <ArrowRight size={11} />
        </Link>
        <button
          onClick={() => onToggleCompare(school.id)}
          className={`px-2.5 py-2 rounded-lg text-xs font-medium border transition-all active:scale-95 ${
            isComparing ? 'bg-primary/10 text-primary border-primary/30' : 'bg-white text-slate-400 border-slate-200 hover:border-primary/30 hover:text-primary'
          }`}
        >
          {isComparing ? '✓' : 'Compare'}
        </button>
      </div>
    </div>
  );
}

export default function SchoolSearchClient() {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'map' | 'shortlist'>('grid');
  const [sortBy, setSortBy] = useState('rating-desc');
  const [compareList, setCompareList] = useState<string[]>([]);
  const [shortlist, setShortlist] = useState<string[]>([]);
  const [showCommutePanel, setShowCommutePanel] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);

  // Track last fired search params to prevent duplicate events on rerenders
  const lastSearchEventRef = useRef<string>('');

  // Persist shortlist in localStorage
  useEffect(() => {
    const saved = localStorage.getItem('schoolmapper_shortlist');
    if (saved) setShortlist(JSON.parse(saved));
  }, []);
  useEffect(() => {
    localStorage.setItem('schoolmapper_shortlist', JSON.stringify(shortlist));
  }, [shortlist]);

  // Sync compareList from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) setCompareList(parsed);
      } catch {
        // ignore
      }
    }
    const handleUpdate = () => {
      const stored2 = localStorage.getItem(STORAGE_KEY);
      if (stored2) {
        try {
          const parsed = JSON.parse(stored2);
          if (Array.isArray(parsed)) setCompareList(parsed);
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('schoolmapper_compare_updated', handleUpdate);
    return () => window.removeEventListener('schoolmapper_compare_updated', handleUpdate);
  }, []);

  // Detect if the search query matches a known community name (case-insensitive, exact or prefix match only)
  const communityFromQuery = useMemo(() => {
    if (!searchQuery.trim()) return '';
    const q = searchQuery.trim().toLowerCase();
    // Exact match first (case-insensitive)
    const exact = DUBAI_COMMUNITIES.find(c => c.toLowerCase() === q);
    if (exact) return exact;
    // Prefix match only: community name starts with the query (minimum 4 chars to avoid false positives)
    // This prevents short queries like "Al" or "Emirates" from matching unintended communities
    if (q.length >= 4) {
      const prefixMatch = DUBAI_COMMUNITIES.find(c => c.toLowerCase().startsWith(q));
      if (prefixMatch) return prefixMatch;
    }
    return '';
  }, [searchQuery]);

  const filteredSchools = useMemo(() => {
    const filtered = applyFilters(SCHOOLS, filters, searchQuery, communityFromQuery);
    const withMinimum = ensureMinimumResults(filtered, filters, SCHOOLS, communityFromQuery);
    const activeCommunity = communityFromQuery || filters.community;
    // When a community is detected from the search bar, always sort by travel time (closest first)
    // so schools genuinely close to that community appear at the top
    const effectiveSortBy = communityFromQuery ? 'travel-asc' : sortBy;
    return applySorting(withMinimum, effectiveSortBy, activeCommunity);
  }, [filters, searchQuery, sortBy, communityFromQuery]);

  // Fire school_search event when search params change — debounced to avoid firing on every keystroke
  useEffect(() => {
    const activeCommunity = communityFromQuery || filters.community;
    // Only fire when there's a meaningful search (community, query, or active filters)
    const hasSearch = !!(activeCommunity || searchQuery.trim() || filters.curricula.length > 0 || filters.khdaRatings.length > 0 || filters.maxCommuteMinutes < 999);
    if (!hasSearch) return;

    const eventKey = JSON.stringify({
      community: activeCommunity,
      query: searchQuery.trim(),
      curricula: filters.curricula,
      khda_rating: filters.khdaRatings,
      fee_filter: (filters.startingFeeMin > 0 || filters.startingFeeMax < 999999) ? `${filters.startingFeeMin}-${filters.startingFeeMax}` : undefined,
      results_count: filteredSchools.length,
    });

    if (eventKey === lastSearchEventRef.current) return;

    const timer = setTimeout(() => {
      lastSearchEventRef.current = eventKey;
      trackSchoolSearch({
        search_location: searchQuery.trim() || undefined,
        community: activeCommunity || undefined,
        curriculum: filters.curricula.length > 0 ? filters.curricula.join(', ') : undefined,
        khda_rating: filters.khdaRatings.length > 0 ? filters.khdaRatings.join(', ') : undefined,
        fee_filter: (filters.startingFeeMin > 0 || filters.startingFeeMax < 999999) ? `${filters.startingFeeMin}-${filters.startingFeeMax}` : undefined,
        results_count: filteredSchools.length,
      });
    }, 800);

    return () => clearTimeout(timer);
  }, [communityFromQuery, filters.community, searchQuery, filters.curricula, filters.khdaRatings, filters.maxCommuteMinutes, filters.startingFeeMin, filters.startingFeeMax, filteredSchools.length]);

  const shortlistedSchools = useMemo(() => SCHOOLS.filter(s => shortlist.includes(s.id)), [shortlist]);

  const handleToggleCompare = useCallback((id: string) => {
    const stored = localStorage.getItem(STORAGE_KEY);
    let current: string[] = [];
    try {
      if (stored) current = JSON.parse(stored);
    } catch {
      // ignore
    }

    if (current.includes(id)) {
      // Remove from compare
      const next = current.filter((x) => x !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event('schoolmapper_compare_updated'));
      setCompareList(next);
      return;
    }

    if (current.length >= MAX_COMPARE) {
      toast.error(`You can compare up to ${MAX_COMPARE} schools at a time`);
      return;
    }

    const school = SCHOOLS.find((s) => s.id === id);
    const next = [...current, id];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event('schoolmapper_compare_updated'));
    setCompareList(next);
    toast.success(`${school?.shortName} added to School Comparison`, {
      action: {
        label: 'View Comparison',
        onClick: () => { window.location.href = '/school-profile-comparison'; },
      },
    });
  }, []);

  const handleShortlist = useCallback((id: string) => {
    setShortlist((prev) => {
      const school = SCHOOLS.find((s) => s.id === id);
      if (prev.includes(id)) {
        toast.info(`${school?.shortName} removed from shortlist`);
        if (school) {
          trackRemoveFromShortlist({ school_name: school.name, school_slug: school.id });
        }
        return prev.filter((x) => x !== id);
      }
      toast.success(`${school?.shortName} added to shortlist ⭐`);
      if (school) {
        trackAddToShortlist({ school_name: school.name, school_slug: school.id });
      }
      return [...prev, id];
    });
  }, []);

  const displaySchools = viewMode === 'shortlist' ? shortlistedSchools : filteredSchools;

  return (
    <>
      <Toaster position="bottom-right" richColors />

      {/* Commute Search Panel */}
      {showCommutePanel && (
        <CommuteSearchPanel
          onClose={() => setShowCommutePanel(false)}
          onSelectCommunity={(community, maxMins) => {
            setFilters(f => ({ ...f, community, maxCommuteMinutes: maxMins }));
            setShowCommutePanel(false);
            toast.success(`Showing schools within ${maxMins} min peak commute from ${community}`);
          }}
        />
      )}

      {/* ── DESKTOP / TABLET layout (sm and above) ── */}
      <div className="hidden sm:block">

        {/* ── DESKTOP HERO ── */}
        <div className="mb-5 pt-1">
          <h1 className="text-2xl font-bold text-[#1e2d5a] leading-tight mb-1">
            Find the right school for where you live
          </h1>
          <p className="text-sm text-slate-500 mb-4">
            Compare schools by real commute time, fees, curriculum and inspection rating.
          </p>

          {/* Search bar + Commute Search in one row */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <SearchHeader
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                viewMode={viewMode === 'shortlist' ? 'grid' : viewMode as 'grid' | 'map'}
                onViewModeChange={(m) => setViewMode(m)}
                sortBy={sortBy}
                onSortChange={setSortBy}
                resultCount={displaySchools.length}
                totalCount={SCHOOLS.length}
                hideResultsRow
              />
            </div>
            {/* Commute Search — prominent SchoolMapper green CTA */}
            <button
              onClick={() => setShowCommutePanel(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-white transition-all duration-150 hover:opacity-90 active:scale-95 shadow-sm shrink-0"
              style={{ backgroundColor: '#1aaa6e', border: '2px solid #1aaa6e' }}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
              </svg>
              Commute Search
            </button>
          </div>

          {/* Results toolbar — count + sort + view toggle in one clean row */}
          <div className="flex items-center justify-between mt-3 gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                Showing <span className="font-semibold text-foreground tabular-nums">{displaySchools.length}</span> of{' '}
                <span className="font-semibold text-foreground tabular-nums">{SCHOOLS.length}</span> schools
              </span>
              {(filters.community || communityFromQuery) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 7 12 7s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
                  {communityFromQuery || filters.community}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-border rounded-lg px-3 py-2 bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              >
                <option value="rating-desc">KHDA Rating: Best First</option>
                <option value="fee-asc">Fee: Low to High</option>
                <option value="fee-desc">Fee: High to Low</option>
                <option value="travel-asc">Travel Time: Nearest First</option>
                <option value="name-asc">Name: A–Z</option>
              </select>
              {/* View toggle */}
              <div className="flex items-center border border-border rounded-lg overflow-hidden bg-white">
                {(['grid', 'map', 'shortlist'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all duration-150 ${viewMode === mode ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`}
                  >
                    {mode === 'grid' ? (
                      <><svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> Grid</>
                    ) : mode === 'map' ? (
                      <><svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg> Map</>
                    ) : (
                      <>⭐ Shortlist {shortlist.length > 0 && `(${shortlist.length})`}</>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <ActiveFilterChips filters={filters} onChange={setFilters} />

        {/* Main content: sidebar + results */}
        <div className="flex gap-5 items-start">
          <FilterSidebar filters={filters} onChange={setFilters} resultCount={filteredSchools.length} />

          <div className="flex-1 min-w-0">
            {viewMode === 'shortlist' ? (
              <div>
                {shortlistedSchools.length === 0 ? (
                  <div className="text-center py-16 text-muted-foreground">
                    <div className="text-4xl mb-3">⭐</div>
                    <p className="font-medium">Your shortlist is empty</p>
                    <p className="text-sm mt-1">Click the star icon on any school card to add it here</p>
                  </div>
                ) : (
                  <SearchResultsGrid
                    schools={shortlistedSchools}
                    communityFilter={communityFromQuery || filters.community}
                    compareList={compareList}
                    onToggleCompare={handleToggleCompare}
                    onSave={handleShortlist}
                    savedList={shortlist}
                    isLoading={false}
                    selectedSchoolId={selectedSchoolId}
                    onSelectSchool={setSelectedSchoolId}
                  />
                )}
              </div>
            ) : viewMode === 'grid' ? (
              <SearchResultsGrid
                schools={filteredSchools}
                communityFilter={communityFromQuery || filters.community}
                compareList={compareList}
                onToggleCompare={handleToggleCompare}
                onSave={handleShortlist}
                savedList={shortlist}
                isLoading={false}
                selectedSchoolId={selectedSchoolId}
                onSelectSchool={setSelectedSchoolId}
              />
            ) : (
              /* Split map layout */
              <div className="flex gap-4 items-start" style={{ height: 620 }}>
                {/* Left: school cards list */}
                <div className="w-80 shrink-0 h-full overflow-y-auto scrollbar-thin space-y-3 pr-1">
                  {filteredSchools.map(school => (
                    <SplitMapSchoolCard
                      key={school.id}
                      school={school}
                      communityFilter={communityFromQuery || filters.community}
                      compareList={compareList}
                      onToggleCompare={handleToggleCompare}
                      onSave={handleShortlist}
                      savedList={shortlist}
                      isSelected={selectedSchoolId === school.id}
                      onSelect={() => setSelectedSchoolId(school.id === selectedSchoolId ? null : school.id)}
                    />
                  ))}
                </div>
                {/* Right: map */}
                <div className="flex-1 h-full bg-white border border-border rounded-xl overflow-hidden shadow-sm">
                  <MapView
                    schools={filteredSchools}
                    communityFilter={communityFromQuery || filters.community}
                    selectedSchoolId={selectedSchoolId}
                    onSelectSchool={setSelectedSchoolId}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── MOBILE layout (below sm) — unchanged ── */}
      <div className="sm:hidden flex flex-col gap-0" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

        {/* ── MOBILE BRANDED HERO ── */}
        <div className="flex flex-col items-center text-center pt-3 pb-3" style={{ width: '100%', boxSizing: 'border-box', paddingLeft: '8px', paddingRight: '8px' }}>
          <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }} className="mb-3">
            <Image
              src="/assets/images/ChatGPT_Image_Jul_24__2026_at_10_20_04_AM-1784874067804.png"
              alt="SchoolMapper — Simplify your school search. Location, Commute Time, Fees, Inspection Rating, Curriculum"
              width={1200}
              height={384}
              className="h-auto object-contain"
              style={{ width: '100%', maxWidth: '340px', margin: '0 auto', display: 'block' }}
              priority
            />
          </div>
          <h1 className="font-bold leading-tight mb-1" style={{ fontSize: '20px', color: '#1e2d5a' }}>
            Find the right school
          </h1>
          <p className="leading-snug" style={{ fontSize: '12px', color: '#94a3b8', maxWidth: '280px' }}>
            Compare schools by location, commute, fees, curriculum and inspection rating.
          </p>
        </div>

        {/* Mobile Search */}
        <div className="mb-2" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', paddingLeft: '8px', paddingRight: '8px' }}>
          <SearchHeader
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            viewMode={viewMode === 'shortlist' ? 'grid' : viewMode as 'grid' | 'map'}
            onViewModeChange={(m) => setViewMode(m)}
            sortBy={sortBy}
            onSortChange={setSortBy}
            resultCount={displaySchools.length}
            totalCount={SCHOOLS.length}
          />
        </div>

        {/* Mobile Filter + Commute row */}
        <div className="flex items-center gap-2 mb-2.5" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', paddingLeft: '8px', paddingRight: '8px' }}>
          <button
            onClick={() => setShowMobileFilters(true)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-border bg-white text-xs font-semibold text-foreground hover:bg-muted transition-colors"
            style={{ minWidth: 0 }}
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 6h18M7 12h10M11 18h2"/></svg>
            Filters
          </button>
          <button
            onClick={() => setShowCommutePanel(true)}
            className="flex-[1.4] flex items-center justify-center gap-1.5 py-2 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-xs font-bold text-white hover:bg-emerald-600 transition-colors shadow-sm"
            style={{ minWidth: 0 }}
          >
            🚗 <span>Commute</span>
          </button>
        </div>

        {/* Mobile Segmented Navigation */}
        <div className="flex rounded-xl border border-border overflow-hidden bg-white mb-3 shadow-sm" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', marginLeft: '8px', marginRight: '8px', width: 'calc(100% - 16px)' }}>
          {[
            { key: 'grid', label: 'Schools' },
            { key: 'map', label: 'Map' },
            { key: 'shortlist', label: `Shortlist${shortlist.length > 0 ? ` (${shortlist.length})` : ''}` },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setViewMode(key as 'grid' | 'map' | 'shortlist')}
              className={`flex-1 py-2 text-xs font-semibold transition-colors truncate ${
                viewMode === key ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'
              }`}
              style={{ minWidth: 0 }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ paddingLeft: '8px', paddingRight: '8px', width: '100%', boxSizing: 'border-box' }}>
          <ActiveFilterChips filters={filters} onChange={setFilters} />
        </div>

        <FilterSidebar
          filters={filters}
          onChange={setFilters}
          resultCount={filteredSchools.length}
          mobileOpen={showMobileFilters}
          onMobileClose={() => setShowMobileFilters(false)}
        />

        <div style={{ paddingLeft: '8px', paddingRight: '8px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          {viewMode === 'shortlist' ? (
            <div>
              {shortlistedSchools.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <div className="text-4xl mb-3">⭐</div>
                  <p className="font-medium">Your shortlist is empty</p>
                  <p className="text-sm mt-1">Tap the heart icon on any school card to save it here</p>
                </div>
              ) : (
                <SearchResultsGrid
                  schools={shortlistedSchools}
                  communityFilter={communityFromQuery || filters.community}
                  compareList={compareList}
                  onToggleCompare={handleToggleCompare}
                  onSave={handleShortlist}
                  savedList={shortlist}
                  isLoading={false}
                />
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <SearchResultsGrid
              schools={filteredSchools}
              communityFilter={communityFromQuery || filters.community}
              compareList={compareList}
              onToggleCompare={handleToggleCompare}
              onSave={handleShortlist}
              savedList={shortlist}
              isLoading={false}
            />
          ) : (
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm" style={{ height: 420 }}>
              <MapView schools={filteredSchools} communityFilter={communityFromQuery || filters.community} />
            </div>
          )}
        </div>
      </div>

      <CompareBar
        compareList={compareList}
        onRemove={(id) => {
          const next = compareList.filter((x) => x !== id);
          setCompareList(next);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
          window.dispatchEvent(new Event('schoolmapper_compare_updated'));
        }}
        onClear={() => {
          setCompareList([]);
          localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
          window.dispatchEvent(new Event('schoolmapper_compare_updated'));
        }}
      />
    </>
  );
}